import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Printer, Plus, Mail, Eye } from 'lucide-react';
import { ordersAPI, customersAPI, notificationsAPI, sitesAPI } from '../../services/api';
import toast from 'react-hot-toast';
import LabelPrint from '../../components/LabelPrint';

interface OrderCompleteModalProps {
    orderId: string;
    onClose: () => void;
}

const OrderCompleteModal: React.FC<OrderCompleteModalProps> = ({ orderId, onClose }) => {
    const navigate = useNavigate();
    const [order, setOrder] = useState<any>(null);
    const [customer, setCustomer] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [showLabel, setShowLabel] = useState<'garment' | 'receipt' | false>(false);
    const [emailSending, setEmailSending] = useState(false);
    const [siteData, setSiteData] = useState<any>(null);

    const handleEmailReceipt = async () => {
        if (!order?._id) return;
        setEmailSending(true);
        try {
            const res = await notificationsAPI.emailReceipt(order._id);
            toast.success(res.data.message || 'Receipt emailed successfully');
        } catch (err: any) {
            toast.error(err.response?.data?.error || 'Failed to email receipt');
        } finally {
            setEmailSending(false);
        }
    };

    useEffect(() => {
        const fetchOrder = async () => {
            try {
                setLoading(true);
                const res = await ordersAPI.getOrder(orderId);
                const orderData = res.data;
                setOrder(orderData);

                if (orderData.customerId && typeof orderData.customerId === 'object') {
                    setCustomer(orderData.customerId);
                } else if (orderData.customerId && typeof orderData.customerId === 'string') {
                    try {
                        const custRes = await customersAPI.getCustomer(orderData.customerId);
                        setCustomer(custRes.data);
                    } catch { /* ignore */ }
                }
            } catch (err) {
                toast.error('Failed to load completed order details');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        if (orderId) fetchOrder();

        // Fetch site data for printer settings
        const siteId = localStorage.getItem('activeSiteId');
        if (siteId) {
            sitesAPI.getSite(siteId).then(res => setSiteData(res.data)).catch(() => {});
        }
    }, [orderId]);

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl p-10 flex flex-col items-center shadow-2xl">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mb-4" />
                    <p className="text-gray-600 font-medium">Loading order details...</p>
                </div>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl p-8 flex flex-col items-center max-w-sm w-full shadow-2xl">
                    <p className="text-red-500 mb-4 font-medium">Error loading order</p>
                    <button onClick={onClose} className="w-full py-4 text-base font-bold rounded-xl bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 transition-colors">
                        Close
                    </button>
                </div>
            </div>
        );
    }

    const labelData = {
        orderNumber: order.orderNumber || '—',
        customerName: customer ? `${customer.firstName} ${customer.lastName}` : 'Walk-in Customer',
        customerPhone: customer?.phone,
        createdAt: order.createdAt,
        dueDate: order.dueDate,
        items: (order.items || []).map((item: any) => ({
            name: item.isCustomItem ? (item.customName || 'Custom item') : `${item.serviceId?.name || 'Unknown Service'}${item.subService?.name ? ` — ${item.subService.name}` : ''}`,
            code: item.serviceId?.code,
            quantity: item.quantity,
            specialInstructions: item.specialInstructions,
        })),
        subtotal: order.subtotal,
        vatAmount: order.vatAmount,
        totalAmount: order.totalAmount || 0,
        paidAmount: order.paidAmount || 0,
        discountAmount: order.discountAmount,
        discountCode: order.appliedDiscount?.code,
        loyaltyPointsEarned: order.loyaltyPointsEarned,
        loyaltyPointsRedeemed: order.loyaltyPointsRedeemed,
        paymentStatus: order.paymentStatus || 'pending',
        status: order.status || 'received',
        specialInstructions: order.specialInstructions,
        siteName: siteData?.name || '',
        sitePhone: siteData?.phone || '',
        siteAddress: siteData?.address ? `${siteData.address}, ${siteData.city || ''}` : '',
        itemCount: order.itemCount || order.items?.length || 0,
        printerWidth: siteData?.settings?.printerWidth || '58mm',
        showReceiptQrCode: siteData?.settings?.showReceiptQrCode === true,
        showVatInfo: siteData?.showVatInfo !== false,
    };

    return (
        <>
            <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
                <div className="bg-white w-full sm:rounded-2xl sm:max-w-md overflow-hidden shadow-2xl">
                    {/* Success header */}
                    <div className="p-8 flex flex-col items-center text-center bg-green-50 border-b border-green-100">
                        <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4 shadow-inner">
                            <CheckCircle className="w-10 h-10" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-1">Order Complete!</h2>
                        <p className="text-gray-500 font-medium">#{order.orderNumber}</p>
                    </div>

                    {/* Order details */}
                    <div className="p-5 space-y-0 divide-y divide-gray-50">
                        <div className="flex justify-between items-center py-3.5">
                            <span className="text-gray-500 text-sm">Customer</span>
                            <span className="font-semibold text-gray-900">{labelData.customerName}</span>
                        </div>
                        <div className="flex justify-between items-center py-3.5">
                            <span className="text-gray-500 text-sm">Items</span>
                            <span className="font-semibold text-gray-900">{labelData.itemCount}</span>
                        </div>
                        <div className="flex justify-between items-center py-3.5">
                            <span className="text-gray-500 text-sm">Total</span>
                            <span className="font-semibold text-gray-900">£{(order.totalAmount || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center py-3.5">
                            <span className="text-gray-500 text-sm">Paid</span>
                            <span className="font-bold text-green-600 text-base">£{(order.paidAmount || 0).toFixed(2)}</span>
                        </div>
                    </div>

                    {/* Action buttons */}
                    <div className="grid grid-cols-4 gap-2 px-5 pb-6 pt-2">
                        <button
                            onClick={() => setShowLabel('receipt')}
                            className="flex items-center justify-center gap-1.5 py-4 text-sm font-semibold bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 active:bg-gray-300 transition-colors border-2 border-gray-100"
                        >
                            <Printer className="w-4 h-4" />
                            Print
                        </button>
                        <button
                            onClick={handleEmailReceipt}
                            disabled={emailSending}
                            className="flex items-center justify-center gap-1.5 py-4 text-sm font-semibold bg-blue-50 text-blue-700 rounded-xl hover:bg-blue-100 active:bg-blue-200 transition-colors border-2 border-blue-100 disabled:opacity-50"
                        >
                            <Mail className="w-4 h-4" />
                            {emailSending ? '...' : 'Email'}
                        </button>
                        <button
                            onClick={() => { onClose(); navigate(`/orders/${order._id}`); }}
                            className="flex items-center justify-center gap-1.5 py-4 text-sm font-semibold bg-purple-50 text-purple-700 rounded-xl hover:bg-purple-100 active:bg-purple-200 transition-colors border-2 border-purple-100"
                        >
                            <Eye className="w-4 h-4" />
                            View
                        </button>
                        <button
                            onClick={onClose}
                            className="flex items-center justify-center gap-1.5 py-4 text-sm font-bold bg-primary-600 text-white rounded-xl hover:bg-primary-700 active:bg-primary-800 transition-colors shadow-md"
                        >
                            <Plus className="w-4 h-4" />
                            New
                        </button>
                    </div>
                </div>
            </div>

            {showLabel && (
                <LabelPrint
                    data={labelData}
                    labelType={showLabel}
                    onClose={() => setShowLabel(false)}
                />
            )}
        </>
    );
};

export default OrderCompleteModal;
